import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  ALERT_CHANNELS,
  ALERT_CONSENT_TEXT,
  departmentName,
  FAVORITE_CATEGORIES,
  isActiveContractor,
  safeNextPath,
  signInProviders,
  type AlertChannel,
  type Me,
  type ProfileUpdate,
  type SignInProviderId,
} from "@neus/shared";
import { api } from "../api/client";
import { useMe, useSignOut } from "../api/hooks";
import { CartIcon, HardHatIcon, KeyIcon, TagIcon, UserIcon } from "../components/Icons";
import { IconCard, LoadError, PageTitle } from "../components/ui/IconCard";
import { SignInButton } from "../components/ui/SignInButton";
import { ContractorStatusNotice } from "./Contractors";
import styles from "./Account.module.css";
import { features } from "../features";

const message = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong. Please try again.");

const safeNext = safeNextPath;
/** A `next` that's an API link (a job order's QR code, /api/qr/…) needs a full page load, not a client route. */
const isApiPath = (path: string) => path.startsWith("/api/");

const SIGN_IN_ERRORS: Record<string, string> = {
  cancelled: "Sign-in was cancelled.",
  expired: "That sign-in took too long. Please try again.",
  failed: "We couldn't sign you in. Please try again.",
};

/** The sign-in providers this build offers (flags signInGoogle and so on), in their order. */
const PROVIDERS = signInProviders(features);

/** "A", "A or B", "A, B or C". */
const orList = (names: string[]) =>
  names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} or ${names.at(-1)}`;

/** The API's sign-in link: Entra External ID's page (the emailed code), or straight to one provider. */
const loginHref = (next: string, idp?: SignInProviderId) =>
  `/api/auth/login?${idp ? `idp=${idp}&` : ""}next=${encodeURIComponent(next)}`;

/** Sign-in happens on Entra External ID's page, or the chosen provider's, then comes back here. */
export function SignInPage() {
  const [params] = useSearchParams();
  const { data: me } = useMe();
  const next = safeNext(params.get("next")) ?? "/account";
  const error = params.get("error");
  useEffect(() => {
    if (me && isApiPath(next)) window.location.replace(next);
  }, [me, next]);
  if (me) return isApiPath(next) ? null : <Navigate to={next} replace />;
  return (
    <div className="container section">
      <div className={styles.narrow}>
        <PageTitle>Sign in</PageTitle>
        <IconCard icon={<KeyIcon />} title="Sign in or create an account">
          <p>
            No password needed: we&rsquo;ll email you a one-time code
            {PROVIDERS.length > 0 && <>, or use your {orList(PROVIDERS.map((p) => p.name))} account</>}. New here? The
            same step creates your account.
          </p>
          {error && <div className="notice notice--warn">{SIGN_IN_ERRORS[error] ?? SIGN_IN_ERRORS.failed}</div>}
          {/* Full page loads: the API redirects to the sign-in page (or the provider's) and back. */}
          <div className={styles.signInOptions}>
            <a className={`btn ${styles.emailSignIn}`} href={loginHref(next)}>
              Continue with email
            </a>
            {PROVIDERS.map((p) => (
              <SignInButton key={p.id} provider={p} href={loginHref(next, p.id)} />
            ))}
          </div>
        </IconCard>
      </div>
    </div>
  );
}

function toUpdate(me: Me): ProfileUpdate {
  return {
    name: me.name,
    phone: me.phone ?? "",
    favoriteCategories: me.favoriteCategories.filter((c) => FAVORITE_CATEGORIES.includes(c)),
    alerts: { optIn: me.alerts.optIn, channel: me.alerts.channel },
    contractor: {
      isContractor: me.contractor.isContractor,
      companyName: me.contractor.companyName ?? "",
      companyPhone: me.contractor.companyPhone ?? "",
      companyEmail: me.contractor.companyEmail ?? "",
    },
  };
}

function ProfileForm({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // ?contractor=1 (from the contractor program page) starts with the contractor box checked.
  const [form, setForm] = useState<ProfileUpdate>(() => {
    const f = toUpdate(me);
    return params.get("contractor") ? { ...f, contractor: { ...f.contractor, isContractor: true } } : f;
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);

  /** Email alerts need the account's email, texts a mobile number, and "both" needs both. */
  const canUse = (channel: AlertChannel) =>
    (channel === "sms" || Boolean(me.email)) && (channel === "email" || Boolean(form.phone?.trim()));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      const updated = await api.updateMe(form);
      qc.setQueryData(["me"], updated);
      setStatus({ ok: true, text: "Saved." });
      const next = safeNext(params.get("next"));
      if (next && isApiPath(next)) window.location.assign(next);
      else if (next) navigate(next);
    } catch (err) {
      setStatus({ ok: false, text: message(err) });
    } finally {
      setSaving(false);
    }
  };

  const endSession = useSignOut();
  const signOut = async (everywhere: boolean) => {
    setStatus(null);
    try {
      await endSession(everywhere);
    } catch {
      // Stay signed in on screen: a failed "everywhere" must not look like it worked.
      setStatus({ ok: false, text: "We couldn't sign you out. Please try again." });
    }
  };

  const toggleFavorite = (slug: string, on: boolean) =>
    setForm((f) => ({
      ...f,
      favoriteCategories: on ? [...f.favoriteCategories, slug] : f.favoriteCategories.filter((c) => c !== slug),
    }));

  return (
    <form className={styles.stack} onSubmit={save}>
      {params.get("welcome") && (
        <div className="notice">Welcome! Tell us a little about yourself, then save. You can change this any time.</div>
      )}

      <IconCard icon={<UserIcon />} title="About you">
        <div className={styles.field}>
          <span>Email</span>
          <span className={styles.value}>{me.email ?? <em className="muted">None shared</em>}</span>
        </div>
        <label className={styles.field}>
          <span>Name</span>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoComplete="name"
            maxLength={100}
          />
        </label>
        <label className={styles.field}>
          <span>Mobile number (optional, for text alerts)</span>
          <input
            className="input"
            type="tel"
            value={form.phone ?? ""}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            autoComplete="tel"
            placeholder="414-555-0123"
          />
        </label>
      </IconCard>

      <IconCard icon={<CartIcon />} title="What do you shop for?">
        <div className={styles.checks}>
          {FAVORITE_CATEGORIES.map((slug) => (
            <label key={slug} className={styles.check}>
              <input
                type="checkbox"
                checked={form.favoriteCategories.includes(slug)}
                onChange={(e) => toggleFavorite(slug, e.target.checked)}
              />
              {departmentName(slug)}
            </label>
          ))}
        </div>
      </IconCard>

      <IconCard icon={<TagIcon />} title="Deals and opportunities">
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={form.alerts.optIn}
            onChange={(e) => setForm({ ...form, alerts: { ...form.alerts, optIn: e.target.checked } })}
          />
          <span>{ALERT_CONSENT_TEXT}</span>
        </label>
        {form.alerts.optIn && (
          <fieldset className={styles.radios}>
            <legend>Send them by</legend>
            {ALERT_CHANNELS.map((channel) => (
              <label key={channel} className={styles.check}>
                <input
                  type="radio"
                  name="alert-channel"
                  value={channel}
                  checked={form.alerts.channel === channel}
                  disabled={!canUse(channel)}
                  onChange={() => setForm({ ...form, alerts: { ...form.alerts, channel } })}
                />
                <span>
                  {channel === "email" ? "Email" : channel === "sms" ? "Text message" : "Both"}
                  {!canUse(channel) && (
                    <span className="muted">
                      {" "}
                      ({channel === "email" ? "your account has no email" : "add a mobile number above"})
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>
        )}
      </IconCard>

      {features.contractorProgram && (
        <IconCard icon={<HardHatIcon />} title="Contractors">
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={form.contractor.isContractor}
              onChange={(e) => setForm({ ...form, contractor: { ...form.contractor, isContractor: e.target.checked } })}
            />
            {/* One element, so the label's gap only separates it from the checkbox. */}
            <span>
              I&rsquo;m a contractor (<Link to="/contractors">what you get</Link>)
            </span>
          </label>
          {me.contractor.isContractor && !isActiveContractor(me) && (
            <ContractorStatusNotice status={me.contractor.status} />
          )}
          {isActiveContractor(me) && (
            <p className={styles.links}>
              <Link to="/contractor/orders">Your job orders</Link>
              <span aria-hidden="true">·</span>
              <Link to="/contractor/orders/new">Start a job order</Link>
            </p>
          )}
          {params.get("contractor") && !me.contractor.isContractor && (
            <div className="notice">
              Add your company&rsquo;s name below, then save to turn on contractor ordering
              {features.contractorApproval ? ". The store reviews new contractor accounts before they can order." : "."}
            </div>
          )}
          {form.contractor.isContractor && (
            <div className={styles.stack}>
              <label className={styles.field}>
                <span>Company name</span>
                <input
                  className="input"
                  value={form.contractor.companyName ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, contractor: { ...form.contractor, companyName: e.target.value } })
                  }
                  autoComplete="organization"
                  required
                  maxLength={120}
                />
              </label>
              <label className={styles.field}>
                <span>Company phone (optional)</span>
                <input
                  className="input"
                  type="tel"
                  value={form.contractor.companyPhone ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, contractor: { ...form.contractor, companyPhone: e.target.value } })
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Company email for company-only deals (optional)</span>
                <input
                  className="input"
                  type="email"
                  value={form.contractor.companyEmail ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, contractor: { ...form.contractor, companyEmail: e.target.value } })
                  }
                />
              </label>
            </div>
          )}
        </IconCard>
      )}

      {status && <div className={status.ok ? "notice" : "notice notice--warn"}>{status.text}</div>}
      <div className={styles.row}>
        <button className="btn" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => signOut(false)}>
          Sign out
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => signOut(true)}>
          Sign out on all devices
        </button>
      </div>
    </form>
  );
}

export function AccountPage() {
  const { data: me, isLoading, isError, refetch } = useMe();
  if (isLoading) {
    return (
      <div className="container section">
        <div className={styles.narrow}>
          <PageTitle>Your account</PageTitle>
          <p className="muted">Loading…</p>
        </div>
      </div>
    );
  }
  // An outage isn't "signed out" (api.me returns null for that), so don't send them to sign in.
  if (isError) {
    return (
      <div className="container section">
        <div className={styles.narrow}>
          <PageTitle>Your account</PageTitle>
          <LoadError onRetry={() => void refetch()}>We couldn&rsquo;t load your account just now.</LoadError>
        </div>
      </div>
    );
  }
  if (!me) return <Navigate to="/account/sign-in?next=%2Faccount" replace />;
  return (
    <div className="container section">
      <div className={styles.narrow}>
        <PageTitle>Your account</PageTitle>
        <ProfileForm key={me.id} me={me} />
      </div>
    </div>
  );
}
